<template>
  <PForm
    nested
    :schema="r$"
    :validate-on="['blur', 'input']"
    class="model-edit-form-rental-intake-item flex flex-col"
  >
    <div class="primary-lighten-1 flex w-full flex-col gap-y-2 rounded-md p-2">
      <div>
        <PFormField sr-only label="Artikelnaam" name="name">
          <PInput v-model="rentalIntakeItem.name" ref="inputName" placeholder="Artikelnaam" />
        </PFormField>
      </div>

      <div>
        <PFormField sr-only label="Eenheid" name="unit">
          <PInputComboboxUnit
            v-model="rentalIntakeItem.unit"
            input-placeholder="Eenheid"
            :disabled="true"
          />
        </PFormField>
      </div>

      <div>
        <PFormField label="Leverancier" sr-only name="supplier">
          <KnownBusinessEntityCombobox
            v-model="rentalIntakeItem.supplier"
            :items="knownBusinessEntities"
            items-key-name="id"
            items-value-name="name"
            input-placeholder="Leverancier"
            creation-option
            grow
            auto-select-newly-created-model
            :creation-model-type="KnownBusinessEntity"
          />
        </PFormField>
      </div>

      <ModelEditFormProjectRentalIntakeItem
        v-if="creationParameters?.project"
        :project="creationParameters.project"
        :rental-intake-item="rentalIntakeItem"
        ref="modelEditFormProjectRentalIntakeItem"
      />

      <div>
        <PFormField label="Eenheidsprijs per dag">
          <PInputNumber
            v-model="rentalIntakeItem.unitPricePerDay"
            placeholder="Eenheidsprijs per dag"
            allow-zero
          >
            <span class="sm:text-sm">&euro;</span>
          </PInputNumber>
        </PFormField>
      </div>

      <div>
        <PFormField label="Corresponderend verhuurartikel" sr-only name="rentalDispenseItem">
          <PInputCombobox
            v-model="rentalIntakeItem.rentalDispenseItem"
            :items="rentalDispenseItems"
            items-key-name="id"
            :items-value-function="(rii: RentalIntakeItem) => `${rii.name} ${rii.getUnitName()}`"
            input-placeholder="Corresponderend verhuurartikel"
            creation-option
            grow
            auto-select-newly-created-model
            :creation-model-type="RentalDispenseItem"
            :creation-parameters="rentalDispenseItemModelFormCreationParameters"
          />
        </PFormField>
      </div>
    </div>
    <PAlertFormValidation :model-value="r$" />
  </PForm>
</template>

<script setup lang="ts">
import PForm from "@/components/form/PForm.vue"
import PAlertFormValidation from "@/components/form/PAlertFormValidation.vue"
import { computed, watchEffect, inject, onMounted, ref } from "vue"
import { useRegle } from "@regle/core"
import PFormField from "@/components/form/PFormField.vue"
import PInput from "@/components/form/PInput.vue"
import PInputCombobox from "@/components/form/PInputCombox.vue"
import PInputNumber from "@/components/form/PInputNumber.vue"
import { RentalIntakeItem } from "@/models/rental-intake-item.model"
import { storeToRefs } from "pinia"
import { useRentalDispenseItemStore } from "@/store/rental-dispense-item.store"
import { RentalDispenseItem } from "@/models/rental-dispense-item.model"
import { requiredValidator } from "@/helper/validation/validators"
import type { RentalIntakeItemController } from "@/controllers/model-controllers/rental-intake-item-controller"
import { formFocusHack } from "@/helper/form-helper"
import { useFormConfigurator } from "@/composables/form/form-configurator"
import PInputComboboxUnit from "@/components/form/PInputComboboxUnit.vue"
import { upperFirst } from "lodash-es"
import { Units } from "@/enums/units.enum"
import { Project } from "@/models/project.model"
import ModelEditFormProjectRentalIntakeItem from "@/components/model_edit_form/ModelEditFormProjectRentalIntakeItem.vue"
import { RentalIntake } from "@/models/rental-intake.model"
import { uniqueRentalIntakeItemValidator } from "@/helper/validation/rental-intake-item-validation.ts"
import { useKnownBusinessEntityStore } from "@/store/known-business-entities.store.ts"
import { KnownBusinessEntity } from "@/models/known-business-entity.model.ts"
import KnownBusinessEntityCombobox from "@/components/known_business_entity/KnownBusinessEntityCombobox.vue"

const inputName = ref()

const knownBusinessEntityStore = useKnownBusinessEntityStore()
const rentalDispenseItemStore = useRentalDispenseItemStore()
const { knownBusinessEntities } = storeToRefs(knownBusinessEntityStore)
const { rentalDispenseItems } = storeToRefs(rentalDispenseItemStore)
const controller: RentalIntakeItemController = inject("rentalIntakeItemController")!

const {
  model: rentalIntakeItem,
  initialText,
  isFormInvalid
} = useFormConfigurator<RentalIntakeItem>({ controller, customSaveHandler })

const creationParameters: { project?: Project; administrativeEvent?: RentalIntake } =
  inject("creationParameters")!
let rentalDispenseItemModelFormCreationParameters = creationParameters
if (creationParameters.administrativeEvent) {
  // In case of editing a rental intake item based on a rental intake processing item, pass down project information of
  // the associated rental dispense.
  rentalDispenseItemModelFormCreationParameters = {
    project: creationParameters.administrativeEvent.associatedRentalDispense?.project
  }
}

onMounted(() => {
  if (initialText) {
    rentalIntakeItem.value.name = upperFirst(initialText)
  }
  formFocusHack(inputName, rentalIntakeItem, "name")

  rentalIntakeItem.value.unit = Units.PIECE
})

const modelEditFormProjectRentalIntakeItem = ref()
async function customSaveHandler(save: () => Promise<void>) {
  await save()
  if (creationParameters.project && modelEditFormProjectRentalIntakeItem.value) {
    await modelEditFormProjectRentalIntakeItem.value.savePriceAgreement()
  }
}
const rules = {
  supplier: { requiredValidator },
  name: { requiredValidator },
  unit: { requiredValidator },
  rentalDispenseItem: { requiredValidator },
  $self: { unique: uniqueRentalIntakeItemValidator(rentalIntakeItem) }
}

const { r$ } = useRegle(
  computed(() => ({
    name: rentalIntakeItem.value.name,
    unit: rentalIntakeItem.value.unit,
    supplier: rentalIntakeItem.value.supplier ? true : undefined,
    rentalDispenseItem: rentalIntakeItem.value.rentalDispenseItem ? true : undefined
  })),
  rules,
  { autoDirty: false }
)

watchEffect(() => {
  isFormInvalid.value = r$.$invalid
})
</script>

<style scoped></style>
